import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { categoryAPI, templateAPI, tenantAPI } from '../services/api'
import { Calendar, ArrowLeft, Save, AlertCircle } from 'lucide-react'
import { OccasionScheduler } from '../components/OccasionScheduler'
import CategoryDropdown from '../components/CategoryDropdown'

const CreateTemplatePage = () => {
  const navigate = useNavigate()
  const occasionSchedulerRef = useRef()
  const [loading, setLoading] = useState(false)
  const [tenants, setTenants] = useState([])
  const [loadingTenants, setLoadingTenants] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [createdTemplateId, setCreatedTemplateId] = useState(null)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    subcategory_id: '',
    tenants: [], // Changed to array for multiselect
    engineVersion: 'v1',
    // Enhanced occasion fields
    isOccasionDateApplicable: false,
    occasionType: 'none',
    occasionStartDate: '',
    occasionEndDate: '',
    occasionStartTime: '00:01', // Default: 12:01 AM
    occasionEndTime: '23:59',   // Default: 11:59 PM
    isRecurringYearly: false,
    weeklyRecurrenceDays: [],
    monthlyRecurrenceDays: [],
    occasionTimezone: 'Asia/Kolkata',
    occasionMetadata: {}
  })

  const [errors, setErrors] = useState({})

  // Optional template_content for the "Create with content + auto-generate" flow.
  const [templateContentText, setTemplateContentText] = useState('')
  const [templateContentError, setTemplateContentError] = useState(null)
  const [templateContentInfo, setTemplateContentInfo] = useState(null)

  // Parse + validate the pasted/uploaded template_content JSON. Returns the
  // parsed object on success, or null (and sets an error) on failure.
  const parseTemplateContent = (text) => {
    const trimmed = (text || '').trim()
    if (!trimmed) return { ok: true, value: null } // optional — empty means normal create
    let parsed
    try {
      parsed = JSON.parse(trimmed)
    } catch (e) {
      return { ok: false, error: 'Invalid JSON: ' + e.message }
    }
    if (!parsed || typeof parsed !== 'object') {
      return { ok: false, error: 'template_content must be a JSON object.' }
    }
    // Accept either a full template_content ({ pages: [...] }) or a wrapper
    // like { template_content: {...} }.
    const content = Array.isArray(parsed.pages) ? parsed : (parsed.template_content || parsed)
    if (!Array.isArray(content.pages)) {
      return { ok: false, error: 'template_content must contain a "pages" array.' }
    }
    return { ok: true, value: content }
  }

  const handleTemplateContentChange = (text) => {
    setTemplateContentText(text)
    const res = parseTemplateContent(text)
    if (!res.ok) {
      setTemplateContentError(res.error)
      setTemplateContentInfo(null)
    } else {
      setTemplateContentError(null)
      if (res.value) {
        const pages = res.value.pages?.length || 0
        let imgWithPrompt = 0
        ;(res.value.pages || []).forEach(p => (p.items || []).forEach(it => {
          if (it && it.type === 'image' && String(it.prompt || '').trim()) imgWithPrompt++
        }))
        setTemplateContentInfo(`${pages} page(s), ${imgWithPrompt} AI image(s) to generate`)
      } else {
        setTemplateContentInfo(null)
      }
    }
  }

  const handleTemplateContentFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => handleTemplateContentChange(String(ev.target?.result || ''))
    reader.onerror = () => setTemplateContentError('Failed to read the file.')
    reader.readAsText(file)
    e.target.value = ''
  }

  useEffect(() => {
    fetchTenants()
  }, [])

  const fetchTenants = async () => {
    try {
      setLoadingTenants(true)
      const response = await tenantAPI.getAll({ isActive: true })

      if (response.data.success) {
        setTenants(response.data.data || [])
      } else {
        setError('Failed to load tenants')
      }
    } catch (error) {
      console.error('Error fetching tenants:', error)
      setError('Failed to load tenants')
    } finally {
      setLoadingTenants(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    console.log('🔄 handleInputChange called:', { name, value, type, checked })
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }
      console.log('📝 Updated formData:', newData)
      return newData
    })

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleTenantChange = (tenantId, isChecked) => {
    console.log('🔄 handleTenantChange called:', { tenantId, isChecked })
    
    setFormData(prev => {
      const newTenants = isChecked
        ? [...prev.tenants, tenantId]
        : prev.tenants.filter(id => id !== tenantId)
      
      const newData = {
        ...prev,
        tenants: newTenants
      }
      
      console.log('📝 Updated formData with tenants:', newData)
      return newData
    })

    // Clear tenant error
    if (errors.tenants) {
      setErrors(prev => ({ ...prev, tenants: '' }))
    }
  }

  const validateForm = () => {
    console.log('🔍 validateForm called')
    console.log('📝 Form data:', formData)
    console.log('🎯 OccasionScheduler ref:', occasionSchedulerRef.current)
    
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required'
      console.log('❌ Name validation failed')
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
      console.log('❌ Description validation failed')
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required'
      console.log('❌ Category validation failed')
    }

    if (!formData.tenants || formData.tenants.length === 0) {
      newErrors.tenants = 'At least one tenant is required'
      console.log('❌ Tenants validation failed')
    }

    // Validate occasion data if enabled
    if (occasionSchedulerRef.current) {
      const occasionConfig = occasionSchedulerRef.current.getConfig()
      console.log('📅 Occasion config from ref:', occasionConfig)
      console.log('🎯 Occasion type:', occasionConfig.occasionType)
      console.log('📅 Is occasion applicable:', occasionConfig.isOccasionDateApplicable)
      
      if (occasionConfig.isOccasionDateApplicable) {
        const { occasionType, occasionStartDate, occasionEndDate, occasionStartTime, occasionEndTime, weeklyRecurrenceDays, monthlyRecurrenceDays } = occasionConfig
        
        console.log('🔍 Validation details:', {
          occasionType,
          occasionStartDate,
          occasionEndDate,
          occasionStartTime,
          occasionEndTime,
          weeklyRecurrenceDays,
          monthlyRecurrenceDays
        })
        
        // Different validation rules for different occasion types
        switch (occasionType) {
          case 'specific_date':
            if (!occasionStartDate) {
              newErrors.occasionStartDate = 'Start date is required for specific date occasions'
              console.log('❌ Specific date start date validation failed')
            }
            if (!occasionStartTime) {
              newErrors.occasionStartTime = 'Start time is required for specific date occasions'
              console.log('❌ Specific date start time validation failed')
            }
            break
            
          case 'date_range':
            if (!occasionStartDate) {
              newErrors.occasionStartDate = 'Start date is required for date range occasions'
              console.log('❌ Date range start date validation failed')
            }
            if (!occasionStartTime) {
              newErrors.occasionStartTime = 'Start time is required for date range occasions'
              console.log('❌ Date range start time validation failed')
            }
            if (!occasionEndDate) {
              newErrors.occasionEndDate = 'End date is required for date range occasions'
              console.log('❌ Date range end date validation failed')
            }
            if (!occasionEndTime) {
              newErrors.occasionEndTime = 'End time is required for date range occasions'
              console.log('❌ Date range end time validation failed')
            }
            if (occasionStartDate && occasionEndDate) {
              if (new Date(occasionStartDate) >= new Date(occasionEndDate)) {
                newErrors.occasionEndDate = 'End date must be after start date'
                console.log('❌ Date range validation failed')
              }
            }
            break
            
          case 'recurring_yearly':
            if (!occasionStartDate) {
              newErrors.occasionStartDate = 'Start date is required for recurring yearly occasions'
              console.log('❌ Recurring yearly start date validation failed')
            }
            if (!occasionStartTime) {
              newErrors.occasionStartTime = 'Start time is required for recurring yearly occasions'
              console.log('❌ Recurring yearly start time validation failed')
            }
            break
            
          case 'weekly':
            console.log('✅ Weekly pattern selected - no start date required')
            if (!weeklyRecurrenceDays || weeklyRecurrenceDays.length === 0) {
              newErrors.weeklyRecurrenceDays = 'At least one day must be selected for weekly recurrence'
              console.log('❌ Weekly recurrence days validation failed')
            }
            // No start date required for weekly patterns
            break
            
          case 'monthly':
            console.log('✅ Monthly pattern selected - no start date required')
            if (!monthlyRecurrenceDays || monthlyRecurrenceDays.length === 0) {
              newErrors.monthlyRecurrenceDays = 'At least one day must be selected for monthly recurrence'
              console.log('❌ Monthly recurrence days validation failed')
            }
            // No start date required for monthly patterns
            break
            
          default:
            console.log('⚠️ Unknown occasion type:', occasionType)
        }
      }
    }

    console.log('🚨 Validation errors:', newErrors)
    setErrors(newErrors)
    const isValid = Object.keys(newErrors).length === 0
    console.log('✅ Form validation result:', isValid)
    return isValid
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log('🚀 handleSubmit called!')
    
    // Prevent multiple submissions
    if (loading) {
      console.log('⏳ Already submitting, ignoring duplicate call')
      return
    }
    
    console.log('📝 Current formData state:', formData)
    console.log('🔍 Form field values:')
    console.log('  - name:', formData.name)
    console.log('  - description:', formData.description)
    console.log('  - category_id:', formData.category_id)
    console.log('  - tenants:', formData.tenants)
    console.log('  - engineVersion:', formData.engineVersion)

    // Validate form before submission
    if (!validateForm()) {
      console.log('❌ Form validation failed')
      return
    }

    console.log('✅ Form validation passed, proceeding with API call...')

    try {
      setLoading(true)
      setError(null)

      // Get occasion data from the scheduler
      let occasionData = {}
      if (occasionSchedulerRef.current) {
        occasionData = occasionSchedulerRef.current.getConfig()
        console.log('📅 Occasion data:', occasionData)
      }

      // Prepare template data (without tenants - they'll be handled separately)
      const templateData = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        subcategory_id: formData.subcategory_id || null,
        engineVersion: formData.engineVersion,
        // Occasion data - handle empty strings properly
        isOccasionDateApplicable: occasionData.isOccasionDateApplicable || false,
        occasionType: occasionData.occasionType || 'none',
        occasionStartDate: (occasionData.isOccasionDateApplicable && occasionData.occasionStartDate) ? occasionData.occasionStartDate : null,
        occasionEndDate: (occasionData.isOccasionDateApplicable && occasionData.occasionEndDate) ? occasionData.occasionEndDate : null,
        occasionStartTime: (occasionData.isOccasionDateApplicable && occasionData.occasionStartTime) ? occasionData.occasionStartTime : null,
        occasionEndTime: (occasionData.isOccasionDateApplicable && occasionData.occasionEndTime) ? occasionData.occasionEndTime : null,
        isRecurringYearly: occasionData.isRecurringYearly || false,
        weeklyRecurrenceDays: occasionData.weeklyRecurrenceDays || [],
        monthlyRecurrenceDays: occasionData.monthlyRecurrenceDays || [],
        occasionTimezone: occasionData.occasionTimezone || 'Asia/Kolkata',
        occasionMetadata: occasionData.occasionMetadata || {},
        // Template type (default to image)
        type: 'image'
      }

      // Check if required fields are present
      const hasRequiredFields = templateData.name && 
                               templateData.description && 
                               templateData.category_id && 
                               formData.tenants && 
                               formData.tenants.length > 0
      
      if (!hasRequiredFields) {
        console.error('❌ Missing required fields:', {
          name: !!templateData.name,
          description: !!templateData.description,
          category_id: !!templateData.category_id,
          tenants: formData.tenants && formData.tenants.length > 0
        })
        console.error('❌ Field values:', {
          name: templateData.name,
          description: templateData.description,
          category_id: templateData.category_id,
          tenants: formData.tenants
        })
        setError('Please fill in all required fields before submitting')
        return
      }
      
      console.log('✅ All required fields are present')

      // Clean up the data - remove empty strings and undefined values
      const cleanedTemplateData = Object.fromEntries(
        Object.entries(templateData).filter(([key, value]) => {
          // Keep all fields as they are properly structured now
          const shouldKeep = true
          if (!shouldKeep) {
            console.log(`🗑️ Removing field "${key}" with value:`, value)
          }
          return shouldKeep
        })
      )
      
      console.log('🧹 Cleaned template data:', cleanedTemplateData)
      console.log('🔍 Data types:', Object.fromEntries(
        Object.entries(cleanedTemplateData).map(([key, value]) => [key, typeof value])
      ))

      console.log('📤 Sending template data:', templateData)
      console.log('🌐 Making API call to:', '/api/v1/templates')
      console.log('🔧 templateAPI object:', templateAPI)
      console.log('🔧 templateAPI.create method:', templateAPI.create)
      console.log('📋 Final data being sent:', cleanedTemplateData)
      console.log('📋 Data as JSON string:', JSON.stringify(cleanedTemplateData, null, 2))
      console.log('🏢 Tenants to be assigned:', formData.tenants)
      console.log('ℹ️ Note: Tenants will be assigned after template creation')

      // Test basic API connectivity first
      try {
        console.log('🧪 Testing API connectivity...')
        const testResponse = await templateAPI.getAll()
        console.log('✅ API connectivity test passed:', testResponse)
      } catch (testError) {
        console.error('❌ API connectivity test failed:', testError)
      }

      // If the user supplied template_content, validate it and route to the
      // create-with-content endpoint (server-side bulk AI generation).
      const tcResult = parseTemplateContent(templateContentText)
      if (!tcResult.ok) {
        setTemplateContentError(tcResult.error)
        setError('Please fix the Template Content JSON before submitting.')
        setLoading(false)
        return
      }
      const withContent = !!tcResult.value
      if (withContent) {
        cleanedTemplateData.template_content = tcResult.value
      }

      const response = withContent
        ? await templateAPI.createWithContent(cleanedTemplateData)
        : await templateAPI.create(cleanedTemplateData)
      console.log('📥 API response:', response)

      // create returns { success: true, ... } (legacy) OR { status: 'success', ... }.
      const ok = response.data?.success === true || response.data?.status === 'success'
      if (ok) {
        console.log('✅ Template created successfully!')
        const templateId = response.data.data?.id
        setCreatedTemplateId(templateId)
        setSuccess(true)
        setTimeout(() => {
          if (withContent) {
            // Background generation is running; go to the dashboard to watch
            // the template's stage (processing → pending/failed).
            navigate('/dashboard')
          } else if (templateId) {
            navigate(`/editor/${templateId}`)
          } else {
            navigate('/dashboard')
          }
        }, 2000)
      } else {
        setError(response.data.message || 'Failed to create template')
      }
    } catch (error) {
      console.error('❌ Error creating template:', error)
      setError('Failed to create template. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const engineVersionOptions = [
    { value: 'v1', label: 'Version 1.0' }
  ]

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Template Created Successfully!</h2>
          <p className="text-gray-600 mb-4">Redirecting to editor...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Create New Template</h1>
            <div className="w-32"></div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Template Information</h2>
            <p className="text-sm text-gray-500 mt-1">
              Provide basic information about your template
            </p>
          </div>

          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          <form 
            onSubmit={(e) => {
              console.log('📝 Form onSubmit triggered!')
              console.log('📝 Form event:', e)
              console.log('📝 Form target:', e.target)
              console.log('📝 Form action:', e.target.action)
              console.log('📝 Form method:', e.target.method)
              try {
                handleSubmit(e)
              } catch (error) {
                console.error('❌ Error in form onSubmit:', error)
                setError('Error in form submission: ' + error.message)
              }
            }} 
            className="p-6 space-y-6"
          >
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Template Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter template name"
                required
              />
              {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe your template"
                required
              />
              {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
            </div>

            <div className="grid grid-cols-1 gap-6">
              <CategoryDropdown
                value={formData.category_id}
                onChange={(value) => setFormData(prev => ({ ...prev, category_id: value, subcategory_id: '' }))}
                placeholder="Select a category"
                type="template"
                isActive={true}
                required={true}
                label="Category"
                error={errors.category_id}
                showSubcategories={true}
                subcategoryValue={formData.subcategory_id}
                onSubcategoryChange={(value) => setFormData(prev => ({ ...prev, subcategory_id: value }))}
                subcategoryPlaceholder="Select a subcategory (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="tenants" className="block text-sm font-medium text-gray-700 mb-1">
                  Tenants *
                </label>
                <div className={`border rounded-lg p-2 ${
                  errors.tenants ? 'border-red-300' : 'border-gray-300'
                } ${loadingTenants ? 'opacity-50' : ''}`}>
                  {loadingTenants ? (
                    <p className="text-sm text-gray-500 py-2">Loading tenants...</p>
                  ) : tenants.length > 0 ? (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {tenants.map((tenant) => (
                        <label key={tenant.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.tenants.includes(tenant.id)}
                            onChange={(e) => handleTenantChange(tenant.id, e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">
                            {tenant.name} ({tenant.domain})
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 py-2">No tenants available</p>
                  )}
                </div>
                {formData.tenants.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.tenants.length} tenant{formData.tenants.length !== 1 ? 's' : ''} selected
                  </p>
                )}
                {errors.tenants && <p className="text-sm text-red-600 mt-1">{errors.tenants}</p>}
              </div>

              <div>
                <label htmlFor="engineVersion" className="block text-sm font-medium text-gray-700 mb-1">
                  Engine Version *
                </label>
                <select
                  id="engineVersion"
                  name="engineVersion"
                  value={formData.engineVersion}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {engineVersionOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>


            {/* Enhanced Occasion Scheduler */}
            <div className="border-t border-gray-200 pt-6">
              <OccasionScheduler
                ref={occasionSchedulerRef}
                value={formData}
                className="mb-6"
              />
            </div>

            {/* Template Content (optional) — enables background AI generation */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-base font-semibold text-gray-900">Template Content (optional)</h3>
                <label className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer">
                  Upload .json
                  <input type="file" accept=".json,application/json" onChange={handleTemplateContentFile} className="hidden" />
                </label>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Paste a template_content JSON (with a <code>pages</code> array) or upload a file.
                When provided, the template is created with stage <strong>processing</strong> and
                AI images are generated in the background; the stage becomes <strong>pending</strong>
                when done (or <strong>failed</strong> on error).
              </p>
              <textarea
                value={templateContentText}
                onChange={(e) => handleTemplateContentChange(e.target.value)}
                placeholder='{ "pages": [ { "items": [ { "type": "image", "prompt": "..." } ] } ] }'
                rows={8}
                spellCheck={false}
                className={`w-full font-mono text-xs border rounded-lg p-3 focus:ring-2 focus:ring-blue-500 ${templateContentError ? 'border-red-400' : 'border-gray-300'}`}
              />
              {templateContentError && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {templateContentError}
                </p>
              )}
              {!templateContentError && templateContentInfo && (
                <p className="mt-1 text-xs text-green-600">✓ {templateContentInfo}</p>
              )}
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading || !!templateContentError}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>
                  {loading
                    ? 'Creating...'
                    : (templateContentText.trim() && !templateContentError ? 'Create & Auto-Generate' : 'Create Template')}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateTemplatePage
